import type { CompanySummary } from "@/Domain/CompanySummary";
import type { DateLetterHeaderAndCount } from "@/Domain/DateLetterHeaderAndCount";
import { ENV_KEYS, getEnvValue } from "@/shared/helpers/envHelper";
import type { AuthUser } from "@aws-amplify/auth";
import { defineStore, type Store } from "pinia";

type MainStore = Store<
  "main",
  {
    userLogin: string;
    companyLogin: string;
    releaseVersionInfo: string;
    UkdProductFilterCriteria: string;
    UkdCompanyFilterCriteria: string;
    companyRcFilterCriteria: string;
    companyName: string;
    refreshByFacilityContactsCounter: number;
    searchTerms: string;
    pageName: string;
    roles: string[] | null;
    isEmulator: boolean;
    balance: number | null;
    isChometzAvailable: boolean;
    companySummary: CompanySummary | null;
    locPickup: DateLetterHeaderAndCount[] | null;
    isParentCompany: boolean;
    user: AuthUser | null;
  },
  {
    isInRole: (state: any) => (role: string) => boolean | undefined;
    isInAnyRole: (state: any) => (roles: string[]) => boolean;
  },
  {
    setUser(user: AuthUser): void;
    userChanged(user: AuthUser): boolean;
    resetPersistedState(): void;
  }
>;

export const useMainStore: () => MainStore = defineStore("main", {
  state: () => {
    return {
      userLogin: "",
      companyLogin: "",
      releaseVersionInfo: "Version " + getEnvValue(ENV_KEYS.BuildVersion),
      UkdProductFilterCriteria: "",
      UkdCompanyFilterCriteria: "",
      companyRcFilterCriteria: "",
      companyName: "",
      refreshByFacilityContactsCounter: 0,
      searchTerms: "",
      pageName: "",
      roles: null as string[] | null,
      isEmulator: false,
      balance: null as number | null,
      isChometzAvailable: false,
      companySummary: null as CompanySummary | null,
      locPickup: null as DateLetterHeaderAndCount[] | null,
      isParentCompany: false,
      user: null as AuthUser | null,
    };
  },
  getters: {
    isInRole: (state) => {
      return (role: string) => state.roles?.includes(role);
    },
    isInAnyRole: (state) => {
      return (roles: string[]) =>
        (state.roles && roles.some((role) => state.roles!.includes(role))) ??
        false;
    },
  },
  actions: {
    setUser(user: AuthUser) {
      this.user = user;
      this.userLogin = user.username;
    },
    userChanged(user: AuthUser) {
      return this.userLogin !== user.username;
    },
    resetPersistedState() {
      this.user = null;
      this.companyLogin = "";
    },
  },
  persist: {
    pick: ["user", "companyLogin"],
    afterHydrate(ctx: any /*PiniaPluginContext<typeof useMainStore>*/) {
      if (ctx.store.user) ctx.store.setUser(ctx.store.user);
    },
  },
});
